// --- Dependencies -------------------------------------------------------------------------------
import { NavLink } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import Skeleton from "@/UI/atoms/Skeleton";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type TyStat = {
    key: string;
    label: string;
    /** Pre-formatted — currency and locale belong to the screen, not to the strip. */
    value: string;
    hint?: string;
    tone?: "neutral" | "brand" | "good" | "warn" | "bad";
    /** Turns the card into a link to the section it summarises. */
    to?: string;
};

type Props = {
    stats: TyStat[];
    loading?: boolean;
};

// --- Constants ----------------------------------------------------------------------------------

/**
 * A hard ceiling, not a guideline. The strip sits above the table on every screen, and its whole value
 * is being readable in one glance before the eye reaches the data — a sixth card pushes the table below
 * the fold and turns a summary into a second thing to scroll through.
 */
const MAX_STATS = 5;

// --- Logic --------------------------------------------------------------------------------------

/**
 * The headline numbers above a list screen. One row, always — on a narrow screen it scrolls sideways
 * rather than wrapping into a block, so the table underneath keeps the room it needs.
 */
export default function StatsSection({ stats, loading }: Props) {
    if (loading)
        return (
            <div className="stats-section" aria-busy="true">
                {Array.from({ length: 4 }, (_, index) => (
                    <Skeleton key={index} className="stats-section-skeleton" />
                ))}
            </div>
        );

    if (!stats.length) return null;

    return (
        <div className="stats-section">
            {stats.slice(0, MAX_STATS).map((stat) => {
                const body = (
                    <>
                        <p className="stats-card-label">{stat.label}</p>
                        <p className="stats-card-value">{stat.value}</p>
                        {stat.hint && <p className="stats-card-hint">{stat.hint}</p>}
                    </>
                );

                return stat.to ? (
                    <NavLink key={stat.key} to={stat.to} className="stats-card stats-card--link" data-tone={stat.tone ?? "neutral"}>
                        {body}
                    </NavLink>
                ) : (
                    <div key={stat.key} className="stats-card" data-tone={stat.tone ?? "neutral"}>
                        {body}
                    </div>
                );
            })}
        </div>
    );
}
